import { useState } from 'react';
import { Button, Flex, Progress, Slider, Tooltip, theme } from 'antd';
import {
	IoCheckmark,
	IoChevronDown,
	IoAddCircleOutline,
	IoChevronUp,
	IoClose,
	IoCloudDoneOutline,
	IoScanOutline,
	IoShieldCheckmarkOutline,
} from 'react-icons/io5';
import styled, { keyframes } from 'styled-components';

import ColorDot from '@components/modal/objectPickerModal/ColorDot';
import BaseText from '@components/typography/BaseText';
import { useToast } from '@contexts/Toast.context';
import { capitalizeFirst } from '@utils/helpers';
import { colormapColor } from '@utils/map/similarity/similarityStyle';

import { saveLabel, savedLabel, unmatchedNote } from './collectionSteps';
import { runCost } from './findCost';
import { Line, Note, PanelRow } from './FindPanel';
import { segmentSavedText, segmentStats, segmentSummary } from './findSegment';
import Score from './Score';
import { SIZE_SPAN, VERDICT } from './useFindResults';
import { hectares } from './useSearchGrid';
import { OBJECT_COLORS } from './useSimilarityMap';

const fadeIn = keyframes`
	from {
		opacity: 0;
	}
`;

const List = styled.ol`
	list-style: none;
	margin: 0;
	padding: 0;
	overflow-y: auto;
	max-height: ${({ theme }) => theme.token.controlHeight * 8}px;
`;

const Row = styled.li`
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.token.paddingXXS}px;
	padding: ${({ theme }) => `${theme.token.paddingXXS}px 0`};
	border-radius: ${({ theme }) => theme.token.borderRadius}px;
	background: ${({ $highlighted, theme }) =>
		$highlighted ? theme.token.controlItemBgHover : 'transparent'};
	transition: background ${({ theme }) => theme.token.motionDurationFast};
	animation: ${fadeIn} ${({ theme }) => theme.token.motionDurationMid}
		${({ theme }) => theme.token.motionEaseOut};

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

// The row itself zooms to the result; accept and reject sit beside it
const RowButton = styled.button`
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: center;
	gap: ${({ theme }) => theme.token.paddingXS}px;
	padding: 0;
	border: 0;
	background: none;
	color: inherit;
	font: inherit;
	text-align: start;
	cursor: pointer;

	&:focus-visible {
		outline: ${({ theme }) =>
			`${theme.token.lineWidthFocus}px solid ${theme.token.colorPrimaryBorder}`};
	}
`;

// The one big number of the panel
const Big = styled.div`
	display: flex;
	align-items: baseline;
	gap: ${({ theme }) => theme.token.paddingXS}px;

	strong {
		font-size: ${({ theme }) => theme.token.fontSizeHeading1}px;
		font-weight: ${({ theme }) => theme.token.fontWeightStrong};
		line-height: 1;
		font-variant-numeric: tabular-nums;
	}

	span {
		font-size: ${({ theme }) => theme.token.fontSizeLG}px;
	}
`;

const plural = (word, n) => (n === 1 || word.endsWith('s') ? word : `${word}s`);
const seconds = ms => `${(ms / 1000).toFixed(1)} s`;

// The labels of a segment run: the server's, else its pieces', else the words searched for
const segmentLabels = (search, results) =>
	search.labels ??
	(results.length ? [...new Set(results.map(r => r.label))] : null) ??
	(search.request?.text ? search.request.text.split(',') : null);

/** How far a running search has got, and Stop */
const Running = ({ search, progress, stop }) => {
	const { token } = theme.useToken();
	return (
		<>
			<Progress
				percent={Math.round((100 * progress.done) / Math.max(progress.total, 1))}
				showInfo={false}
				size="small"
				strokeColor={token.colorPrimary}
			/>
			<Line>
				<Note aria-live="polite">
					{progress.done} of {progress.total} squares
					{progress.msPerTile ? ` · ${seconds(progress.msPerTile)} each` : ''}
				</Note>
				{/* A stopped search finishes its square first */}
				<Button size="small" onClick={stop} disabled={search.stopping}>
					{search.stopping ? 'Stopping…' : 'Stop'}
				</Button>
			</Line>
		</>
	);
};

/**
 * Found, for a segment run (roads, rivers traced): how much of it, "12.4 km of road" ("14.2 ha of
 * lake"), its pieces and the gaps bridged, and what it cost.
 */
const SegmentFound = ({ find }) => {
	const { search, progress, results, stop } = find;
	const running = Boolean(search.running);
	const summary = segmentSummary(
		segmentStats(search.stats, results),
		segmentLabels(search, results)
	);
	const cost = running ? null : runCost(search.cost);
	return (
		<PanelRow label="Found">
			<Big aria-live="polite" aria-atomic="true">
				<strong>{summary.value}</strong>
				<span>
					{summary.unit}
					{cost && ` · ${cost}`}
				</span>
			</Big>
			<Note>{summary.details}</Note>
			{running && progress && <Running search={search} progress={progress} stop={stop} />}
			{running && !search.plan && <Note>Planning the squares…</Note>}
			{search.error && <Note>{search.error}</Note>}
		</PanelRow>
	);
};

/** Found: the big count, over how much ground, and how far a running search has got. */
const Found = ({ find }) => {
	const { token } = theme.useToken();
	const { search, progress, results, decisions, stop } = find;
	const plan = search.plan;
	// Boxes removed by hand, or with their square cleared, no longer count; nor do those the size filter hides
	const removed = results.filter(result => decisions[result.id] === 'rejected').length;
	const found = results.filter(result => !result.preview).length - removed;
	// Each object's count, in its colour, when the search was for several ("car 60 · house 32")
	const perObject = Object.values(
		results.reduce((acc, result) => {
			if (result.preview || decisions[result.id] === 'rejected') return acc;
			const entry = acc[result.group] ?? { group: result.group, label: result.label, n: 0 };
			return { ...acc, [result.group]: { ...entry, n: entry.n + 1 } };
		}, {})
	).sort((a, b) => a.group - b.group);
	const several = (search.labels?.length ?? 0) > 1 || perObject.length > 1;
	// A word searched for or a model's class, or the examples' name; unnamed examples' boxes are just
	// "found"
	const labelled = search.kind === 'query' || search.kind === 'model';
	const name = several ? null : labelled ? results[0]?.label : search.name;
	const running = Boolean(search.running);
	// What the run cost, once it ended and the server said ("803 trees · $0.21")
	const cost = running ? null : runCost(search.cost);

	return (
		<PanelRow label="Found">
			<Big aria-live="polite" aria-atomic="true">
				<strong>{found}</strong>
				<span>
					{name ? plural(name, found) : 'found'}
					{cost && ` · ${cost}`}
				</span>
			</Big>
			{several && (
				<Flex wrap gap="small">
					{perObject.map(({ group, label, n }) => (
						<Flex key={group} align="center" gap={token.sizeXXS}>
							<ColorDot
								shape="round"
								size={token.fontSizeSM}
								color={OBJECT_COLORS[group % OBJECT_COLORS.length]}
							/>
							<BaseText>
								{n} {plural(label, n)}
							</BaseText>
						</Flex>
					))}
				</Flex>
			)}
			{running && progress && <Running search={search} progress={progress} stop={stop} />}
			{running && !plan && <Note>Planning the squares…</Note>}
			{!running && plan && (
				<Note>
					{search.stopped
						? `in ${progress.done} of ${plan.tiles.length} squares (stopped)`
						: `in ${plan.tiles.length} ${plural('square', plan.tiles.length)}`}{' '}
					· {hectares(plan, search.request?.polygon)}
					{removed > 0 && ` · ${removed} removed`}
				</Note>
			)}
			{search.error && <Note>{search.error}</Note>}
		</PanelRow>
	);
};

const SIZE_BINS = 28;
const metres = m => (m < 1 ? m.toFixed(2) : m < 10 ? m.toFixed(1) : String(Math.round(m)));

// How many boxes of each size, on a log scale: the bars in range in the object's colour
const Bars = styled.div`
	display: flex;
	align-items: flex-end;
	gap: ${({ theme }) => theme.token.lineWidth}px;
	height: ${({ theme }) => theme.token.sizeLG}px;
	margin: 0 ${({ theme }) => theme.token.sizeXS}px;
`;

const Bar = styled.div`
	flex: 1;
	min-height: ${({ theme }) => theme.token.lineWidth}px;
	height: ${({ $height }) => $height * 100}%;
	border-radius: ${({ theme }) => theme.token.lineWidth}px;
	background: ${({ $inRange, $color, theme }) =>
		$inRange ? $color : theme.token.colorFillSecondary};
`;

/**
 * One object's size filter: its boxes' sizes as bars, and the range kept. The range starts at the
 * object's usual size (Claude's for a word, measured from the examples) ÷ and × SIZE_SPAN.
 */
const SizeRow = ({ label, sizes, expected, range, hidden, several, color, onChange }) => {
	const { token } = theme.useToken();
	const lo = Math.min(...sizes, expected ? expected / SIZE_SPAN : Infinity) / 1.25;
	const hi = Math.max(...sizes, expected ? expected * SIZE_SPAN : 0) * 1.25;
	const span = Math.log(hi / lo) || 1;
	const toPosition = m => Math.round((Math.log(m / lo) / span) * 1000);
	const toMetres = position => lo * Math.exp((position / 1000) * span);
	const [min, max] = range ?? [lo, hi];
	const bins = new Array(SIZE_BINS).fill(0);
	sizes.forEach(size => {
		const k = Math.floor((toPosition(size) / 1000) * SIZE_BINS);
		bins[Math.min(SIZE_BINS - 1, Math.max(0, k))] += 1;
	});
	const tallest = Math.max(...bins, 1);
	const binInRange = k => {
		const m = toMetres(((k + 0.5) / SIZE_BINS) * 1000);
		return m >= min && m <= max;
	};
	return (
		<Flex vertical gap={token.sizeXXS}>
			<Line>
				<Flex align="center" gap={token.sizeXXS}>
					{several && <ColorDot shape="round" size={token.fontSizeSM} color={color} />}
					<Note>
						{several ? `${label} ` : ''}
						{range ? `${metres(min)}–${metres(max)} m` : 'All sizes'}
						{hidden ? ` · ${hidden} hidden` : ''}
					</Note>
				</Flex>
				{expected && <Note>usual {metres(expected)} m</Note>}
			</Line>
			<Bars aria-hidden>
				{bins.map((n, k) => (
					<Bar key={k} $height={n / tallest} $inRange={binInRange(k)} $color={color} />
				))}
			</Bars>
			<Slider
				range
				min={0}
				max={1000}
				value={[toPosition(Math.max(min, lo)), toPosition(Math.min(max, hi))]}
				tooltip={{ formatter: position => `${metres(toMetres(position))} m` }}
				onChange={([from, to]) => onChange([toMetres(from), toMetres(to)])}
				aria-label={`Sizes of ${label} to keep`}
			/>
		</Flex>
	);
};

/** Size: per object, hide the boxes far from its usual size (a whole car park found as one "car"). */
const SizeFilter = ({ find }) => {
	const { token } = theme.useToken();
	const { sizeGroups, setSizeRange } = find;
	if (sizeGroups.length === 0) return null;
	const several = sizeGroups.length > 1;
	return (
		<PanelRow label="Size">
			{sizeGroups.map(group => (
				<SizeRow
					key={group.group}
					{...group}
					several={several}
					color={
						several
							? OBJECT_COLORS[group.group % OBJECT_COLORS.length]
							: token.colorPrimary
					}
					onChange={range => setSizeRange(group.group, range)}
				/>
			))}
		</PanelRow>
	);
};

/**
 * Review: tick the squares whose boxes are right (on the map, or with the keys), clear the ones
 * holding none; then save the ticked ones.
 */
const Review = ({ find }) => {
	const { search, results, decisions, tileReview, selectedTile, reviewTile } = find;
	const { tickableInView, tickAllInView, untickAll } = find;
	const tiles = search.plan?.tiles ?? [];
	const verdicts = Object.values(tileReview).filter(Boolean);
	const ticked = verdicts.length;
	const left = tiles.length - ticked;
	const selected = tiles.find(tile => tile.i === selectedTile);
	const inSelected = results.filter(
		result =>
			result.tile === selectedTile && !result.preview && decisions[result.id] !== 'rejected'
	).length;
	const verdict = selected ? tileReview[selected.i] : undefined;
	const canTick = (tickableInView ?? []).filter(i => !tileReview[i]).length;

	return (
		<>
			<PanelRow label="Review">
				<Flex align="baseline" gap="small">
					<BaseText strong>{ticked} ticked</BaseText>
					<Note>· {left} to go</Note>
				</Flex>
				{selected && (
					<Line>
						<Note>
							This square · {inSelected}{' '}
							{search.kind === 'segment'
								? plural('piece', inSelected)
								: inSelected === 1
									? 'box'
									: 'boxes'}
						</Note>
						<Flex gap="small">
							<Tooltip title="Tick: its boxes are right">
								<Button
									size="small"
									color="green"
									variant={verdict === VERDICT.TICKED ? 'solid' : 'outlined'}
									icon={<IoCheckmark />}
									aria-label="Tick this square"
									onClick={() => reviewTile(selected.i, VERDICT.TICKED)}
								/>
							</Tooltip>
							<Tooltip title="Clear: it holds none, so reject its boxes and tick it as empty">
								<Button
									size="small"
									color="danger"
									variant={verdict === VERDICT.CLEARED ? 'solid' : 'outlined'}
									icon={<IoClose />}
									aria-label="Clear this square"
									onClick={() => reviewTile(selected.i, VERDICT.CLEARED)}
								/>
							</Tooltip>
						</Flex>
					</Line>
				)}
			</PanelRow>
			<PanelRow>
				<Flex gap="small">
					<Tooltip
						title={
							tickableInView === null
								? 'Zoom in until the boxes are clear, then tick what you see'
								: 'Ticks the squares wholly on screen'
						}>
						<Button
							size="large"
							style={{ flex: 1 }}
							disabled={!canTick}
							onClick={tickAllInView}>
							Tick all in view{canTick ? ` (${canTick})` : ''}
						</Button>
					</Tooltip>
					{ticked > 0 && (
						<Button size="large" type="text" onClick={untickAll}>
							Untick all
						</Button>
					)}
				</Flex>
				<Tooltip title="Saving comes with Objects: the ticked squares become examples of the Object">
					<Button type="primary" size="large" block disabled>
						Save {ticked} {plural('square', ticked)}
					</Button>
				</Tooltip>
			</PanelRow>
		</>
	);
};

// A save that failed, in the panel's words; the server's own for one it refused for a reason (the
// collection holds none of the objects found, the search was saved elsewhere already)
const saveMessage = error =>
	error?.status === 403
		? 'You may not save results in this project.'
		: error?.status === 404
			? 'This search, or the collection, is gone from the server; run it again to save it.'
			: [409, 422].includes(error?.status) && error?.message
				? error.message
				: 'The results could not be saved just now. Try again in a moment.';

/**
 * Save: the results, without the boxes taken out by hand or with their square cleared, become a job
 * of the project (under Jobs, and in notifications); with a collection chosen, they go into its
 * objects, and the area searched joins its analyzed area. Once saved, says so (and offers to show
 * the collection's results).
 * @param {Object} props
 * @param {Object} props.find - useFindResults
 * @param {Object|null} [props.saveTo] - The collection chosen in Find
 * @param {Function} [props.onViewCollection] - (collection uuid) → void
 */
const Save = ({ find, saveTo = null, onViewCollection }) => {
	const { addToast } = useToast();
	const { search, results, decisions, runUuid, saveResults, saving, saveError } = find;
	const found = results.filter(r => !r.preview && decisions[r.id] !== 'rejected').length;
	const saved = search.saved;
	const segment = search.kind === 'segment';
	const name =
		(saved?.labels?.length === 1 && saved.labels[0]) ||
		(search.labels?.length === 1 && search.labels[0]) ||
		(new Set(results.map(r => r.label)).size === 1 && results[0]?.label) ||
		'box';
	if (saved) {
		const into = saved.collection;
		const unmatched = unmatchedNote(saved);
		return (
			<PanelRow>
				<Button size="large" block disabled icon={<IoCloudDoneOutline />}>
					{segment && !into
						? segmentSavedText(
								segmentStats(search.stats, results),
								segmentLabels(search, results)
							)
						: savedLabel(saved, name)}
				</Button>
				{into && onViewCollection && (
					<Button block onClick={() => onViewCollection(into.uuid)}>
						Show {into.name} on the map
					</Button>
				)}
				{unmatched && <Note>{unmatched}</Note>}
			</PanelRow>
		);
	}
	if (!runUuid || found === 0) return null;
	const onSave = collectionUuid =>
		saveResults({ collectionUuid })
			.then(result =>
				addToast({
					id: 'find-saved',
					bg: 'success',
					title: result?.collection
						? `Saved to ${result.collection.name}`
						: 'Results saved',
					message: result?.collection
						? `Its analyzed area now includes this search. It appears under Jobs and in notifications.`
						: 'Saved to the project. It appears under Jobs and in notifications.',
				})
			)
			.catch(() => null);
	return (
		<PanelRow>
			<Button
				type="primary"
				size="large"
				block
				loading={saving}
				disabled={saving}
				onClick={() => onSave(saveTo?.uuid)}>
				{saving ? 'Saving…' : saveLabel(saveTo)}
			</Button>
			{saveTo && !saving && (
				<Button type="link" size="small" onClick={() => onSave(undefined)}>
					Save to the project only
				</Button>
			)}
			{saveError && <Note role="alert">{saveMessage(saveError)}</Note>}
		</PanelRow>
	);
};

/** Boxes: outline them all, or list them. */
/**
 * Missed: add a box the search missed. A click on the object lets SAM 3 find its box; a drag is taken
 * as drawn. With several objects, pick which one it is first.
 */
const Missed = ({ find }) => {
	const { token } = theme.useToken();
	const { adding, addingBusy, startAdding, stopAdding, search, results } = find;
	const labels =
		search.labels ??
		[...new Map(results.map(r => [r.group, r.label])).entries()]
			.sort(([a], [b]) => a - b)
			.map(([, label]) => label);
	const several = labels.length > 1;

	if (!adding) {
		return (
			<PanelRow label="Missed">
				<Flex>
					<Button icon={<IoAddCircleOutline />} onClick={() => startAdding(0)}>
						Add missed
					</Button>
				</Flex>
			</PanelRow>
		);
	}
	return (
		<PanelRow label="Missed">
			{several && (
				<Flex wrap gap="small">
					{labels.map((label, group) => (
						<Button
							key={group}
							size="small"
							variant={adding.group === group ? 'solid' : 'outlined'}
							icon={
								<ColorDot
									shape="round"
									size={token.fontSizeSM}
									color={OBJECT_COLORS[group % OBJECT_COLORS.length]}
								/>
							}
							onClick={() => startAdding(group)}>
							{label}
						</Button>
					))}
				</Flex>
			)}
			<Note>
				{addingBusy
					? 'Finding its outline…'
					: `Click a missed ${adding.label}, or drag a box around it.`}
			</Note>
			<Flex justify="flex-end">
				<Button size="small" type="primary" onClick={stopAdding}>
					Done
				</Button>
			</Flex>
		</PanelRow>
	);
};

const Boxes = ({ find, colors }) => {
	const { results, decisions, keep, toggleRemoved, hoveredId, setHoveredId, zoomTo } = find;
	const { outlining, outlineAll } = find;
	const [showList, setShowList] = useState(false);
	const outliningNow = Boolean(outlining && !outlining.finished);
	if (results.length === 0) return null;

	return (
		<PanelRow label="Boxes">
			<Flex align="center" gap="small">
				<Button
					type="text"
					icon={<IoScanOutline />}
					loading={outliningNow}
					onClick={outlineAll}>
					{outliningNow
						? `Outlining ${outlining.done}/${outlining.total}`
						: outlining?.finished
							? 'Outline again'
							: 'Outline all'}
				</Button>
				<Button
					type="text"
					icon={showList ? <IoChevronUp /> : <IoChevronDown />}
					iconPosition="end"
					onClick={() => setShowList(shown => !shown)}>
					List {results.length}
				</Button>
			</Flex>
			{showList && (
				<List>
					{results.map(result => {
						const decision = decisions[result.id];
						const label = capitalizeFirst(result.label);
						return (
							<Row
								key={result.id}
								$highlighted={result.id === hoveredId}
								onMouseEnter={() => setHoveredId(result.id)}
								onMouseLeave={() => setHoveredId(null)}>
								<RowButton type="button" onClick={() => zoomTo(result)}>
									<BaseText
										ellipsis
										delete={decision === 'rejected'}
										type={decision === 'rejected' ? 'secondary' : undefined}>
										{label}
									</BaseText>
									<Score
										value={result.score}
										color={colormapColor(colors, result.score)}
									/>
									{result.kept === true && (
										<Tooltip title="Double-checked">
											<BaseText type="success">
												<IoShieldCheckmarkOutline aria-label="Double-checked" />
											</BaseText>
										</Tooltip>
									)}
								</RowButton>
								<Button
									color={decision === 'accepted' ? 'primary' : 'default'}
									variant={decision === 'accepted' ? 'solid' : 'text'}
									size="small"
									icon={<IoCheckmark />}
									aria-label={`Keep ${label}`}
									aria-pressed={decision === 'accepted'}
									onClick={() => keep(result.id)}
								/>
								<Button
									color={decision === 'rejected' ? 'danger' : 'default'}
									variant={decision === 'rejected' ? 'solid' : 'text'}
									size="small"
									icon={<IoClose />}
									aria-label={`Remove ${label}`}
									aria-pressed={decision === 'rejected'}
									onClick={() => toggleRemoved(result)}
								/>
							</Row>
						);
					})}
				</List>
			)}
		</PanelRow>
	);
};

/**
 * What a search found, as rows of the Find panel: the count over the area, the review of its
 * squares, and its boxes.
 */
const FindResults = ({ find, colors, saveTo = null, onViewCollection }) => {
	const { search } = find;
	if (!search) return null;
	const reviewable = !search.running && (search.plan?.tiles.length ?? 0) > 0;
	// A search that ended (done, or stopped with what its squares found) may be saved
	const savable = !search.running && !search.error && search.kind !== 'picked';
	// Roads and rivers traced, not boxed: no sizes, outlines, missed boxes or list of boxes
	if (search.kind === 'segment') {
		return (
			<>
				<SegmentFound find={find} />
				{reviewable && <Review find={find} />}
				{savable && (
					<Save find={find} saveTo={saveTo} onViewCollection={onViewCollection} />
				)}
			</>
		);
	}

	return (
		<>
			<Found find={find} />
			<SizeFilter find={find} />
			{reviewable && <Review find={find} />}
			{!search.running && search.plan && <Missed find={find} />}
			{!search.running && <Boxes find={find} colors={colors} />}
			{savable && <Save find={find} saveTo={saveTo} onViewCollection={onViewCollection} />}
		</>
	);
};

export default FindResults;
